import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Form, Input, Select, Space, Table, Tag, Typography } from 'antd'
import { menuAdminRequest, errorMessage } from './api'
import { METHODS, MODES, relationKey } from './model'
import { indexNavigation, initialMenu } from './navigation'
import MenuNavigation from './MenuNavigation'
import RelationEditor from './RelationEditor'
import HelpHint from './HelpHint'

// 2026-10-20 coder(lq): 左树只选择读取范围；筛选后代由服务端按真实菜单与当前系统分组计算，不改变授权。
export default function RelationPanel({ settings, revision, onReview }) {
    const [form] = Form.useForm()
    const [query, setQuery] = useState({ node: 'ALL', page: 1, size: 20 })
    const [data, setData] = useState({ items: [], total: 0 })
    const [loading, setLoading] = useState(false)
    const [listError, setListError] = useState('')
    const [editor, setEditor] = useState(null)
    const [root, setRoot] = useState(null)
    const [navigationLoading, setNavigationLoading] = useState(true)
    const [navigationError, setNavigationError] = useState('')
    const [refresh, setRefresh] = useState(0)
    const sequence = useRef(0)
    const navigation = useMemo(() => indexNavigation(root), [root])
    useEffect(() => {
        let active = true
        setNavigationLoading(true); setNavigationError('')
        menuAdminRequest('navigation').then(result => {
            if (!active) return
            setRoot(result)
            const available = indexNavigation(result).nodes
            setQuery(previous => available.has(previous.node) ? previous : { ...previous, node: 'ALL', page: 1 })
        }).catch(error => { if (active) { setRoot(null); setNavigationError(errorMessage(error)) } })
            .finally(() => { if (active) setNavigationLoading(false) })
        return () => { active = false }
    }, [revision, refresh, settings.effectivePolicyJson])
    useEffect(() => {
        const id = ++sequence.current
        setLoading(true); setListError(''); setData({ items: [], total: 0 })
        menuAdminRequest('relations', { params: query }).then(result => {
            if (id === sequence.current) setData(result)
        }).catch(error => { if (id === sequence.current) setListError(errorMessage(error)) })
            .finally(() => { if (id === sequence.current) setLoading(false) })
        return () => { sequence.current += 1 }
    }, [query, revision, refresh])
    const selected = navigation.nodes.get(query.node)
    const trail = navigation.trails.get(query.node) || ['全部系统']
    const selectNode = node => setQuery(previous => ({ ...previous, node, page: 1 }))
    const add = () => setEditor({ current: null, initial: initialMenu(selected) })
    const columns = [
        { title: '系统 / 来源', render: (_, row) => <><div>{row.system === 'UNCONFIGURED' ? '未分组' : row.system}</div><small>{row.authority} / {row.service_id}</small></> },
        { title: '菜单', render: (_, row) => <>{row.menuName || '菜单名称待核对'}<div>#{row.resource_id}</div></> },
        { title: '方法', dataIndex: 'http_method', width: 90, render: value => <Tag>{value}</Tag> },
        { title: '接口路径', dataIndex: 'api_path', render: value => <code className="menu-api-path">{value}</code> },
        { title: '系统模式', dataIndex: 'mode', width: 110, render: value => MODES[value] || value },
        { title: '操作', width: 160, render: (_, row) => <Space><Button onClick={() => setEditor({ current: row })}>编辑</Button>
            <Button danger onClick={() => onReview('DELETE', row, null)}>删除</Button></Space> },
    ]
    return <section>
        <div className="menu-api-relation-layout">
            <MenuNavigation root={root} selectedKey={query.node} loading={navigationLoading} error={navigationError}
                onRetry={() => setRefresh(value => value + 1)} onSelect={selectNode} />
            <div className="menu-api-relation-content" aria-label="当前范围接口关系">
                <div className="menu-api-scope-heading"><div>
                    <Typography.Title level={3}>{selected ? selected.title : '接口关系'}
                        <HelpHint label="菜单接口关系说明">手工修正的是菜单与接口关系，不会修改用户的角色或菜单授权。未映射接口当前只记录缺口，不是默认拒绝。
                            {selected && selected.kind === 'CONTEXT' && '此目录仅用于展示层级，右侧只汇总当前系统的后代；目录不会自动获得本系统分组。'}
                            {selected && selected.kind.startsWith('INVALID') && '此处包含停用、缺失或未读取菜单的旧关系；CMS权限源未配置时也会出现在此。请核对来源后修正，不要直接全部删除。'}
                        </HelpHint></Typography.Title>
                    <Typography.Text type="secondary">{trail.join(' / ')}{selected && selected.children.length > 0 ? '（包含下级菜单）' : ''}</Typography.Text>
                </div><Button type="primary" disabled={!root || navigationLoading} onClick={add}>
                    {selected && selected.canAdd ? '给当前菜单新增接口' : '新增关系'}</Button></div>
                <Form form={form} layout="inline" className="menu-api-filters" onFinish={values => setQuery({
                    ...Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== '')),
                    node: query.node, page: 1, size: query.size,
                })}>
                    <Form.Item name="method" label="方法"><Select allowClear placeholder="全部方法" className="menu-api-select" options={METHODS.map(value => ({ value }))} /></Form.Item>
                    <Form.Item name="path" label="接口"><Input maxLength={128} placeholder="输入路径片段" /></Form.Item>
                    <Form.Item><Space><Button htmlType="submit">查询</Button>
                        <Button onClick={() => { form.resetFields(); setQuery({ node: query.node, page: 1, size: query.size }) }}>重置筛选</Button></Space></Form.Item>
                </Form>
                {listError ? <Alert type="error" showIcon message={listError}
                    action={<Button size="small" onClick={() => setRefresh(value => value + 1)}>重试</Button>} /> :
                    <Table rowKey={relationKey} dataSource={data.items} columns={columns} loading={loading} scroll={{ x: 1000 }}
                        pagination={{ current: query.page, pageSize: query.size, total: data.total, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
                            showTotal: total => `共 ${total} 条关系`, onChange: (page, size) => setQuery({ ...query, page, size }) }}
                        locale={{ emptyText: selected && selected.canAdd ? '当前菜单没有匹配关系，可清空筛选或点击“给当前菜单新增接口”。' : '当前范围没有匹配关系，请切换菜单或调整筛选。' }} />}
            </div>
        </div>
        <RelationEditor visible={!!editor} current={editor && editor.current} initial={editor && editor.initial} onCancel={() => setEditor(null)}
            onReview={after => { onReview(editor.current ? 'EDIT' : 'ADD', editor.current, after); setEditor(null) }} />
    </section>
}
